package com.bulut.mecitogullari.ui.theme

import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Typography
import androidx.compose.material3.lightColorScheme
import androidx.compose.runtime.Composable
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.sp

/**
 * Design language: modern finance/accounting app.
 * Neutral dark navy + gray UI, green for income, red for expenses.
 */
object AppColors {
    val Navy = Color(0xFF1E293B)
    val NavyDeep = Color(0xFF0F172A)

    val Income = Color(0xFF16A34A)
    val OverAllIncome = Color(0xFF51A316)
    val IncomeContainer = Color(0xFFDCFCE7)
    val Expense = Color(0xFFDC2626)
    val ExpenseContainer = Color(0xFFFEE2E2)
    val Reserved = Color(0xFFD97706)
    val ReservedContainer = Color(0xFFFEF3C7)

    val Background = Color(0xFFF1F5F9)
    val Surface = Color(0xFFFFFFFF)
    val SurfaceMuted = Color(0xFFF8FAFC)
    val TextPrimary = Color(0xFF0F172A)
    val TextSecondary = Color(0xFF64748B)
    val Outline = Color(0xFFE2E8F0)

    /** Cover accent choices for projects (stored as hex in Project.coverColorHex). */
    val CoverOptions = listOf(
        "#0EA5E9", "#16A34A", "#D97706", "#DC2626", "#7C3AED", "#0F766E", "#1E293B",
    )
}

fun parseHexColor(hex: String): Color {
    val clean = hex.removePrefix("#")
    val value = clean.toLongOrNull(16) ?: return AppColors.Navy
    return when (clean.length) {
        6 -> Color(0xFF000000 or value)
        8 -> Color(value)
        else -> AppColors.Navy
    }
}

private val ColorScheme = lightColorScheme(
    primary = AppColors.Navy,
    onPrimary = Color.White,
    primaryContainer = Color(0xFFE2E8F0),
    onPrimaryContainer = AppColors.NavyDeep,
    secondary = AppColors.TextSecondary,
    onSecondary = Color.White,
    background = AppColors.Background,
    onBackground = AppColors.TextPrimary,
    surface = AppColors.Surface,
    onSurface = AppColors.TextPrimary,
    surfaceVariant = AppColors.SurfaceMuted,
    onSurfaceVariant = AppColors.TextSecondary,
    error = AppColors.Expense,
    onError = Color.White,
    errorContainer = AppColors.ExpenseContainer,
    onErrorContainer = AppColors.Expense,
    outline = AppColors.Outline,
    outlineVariant = AppColors.Outline,
)

private val AppTypography = Typography().let { base ->
    base.copy(
        // Large, highly readable numerals for balances and totals.
        displaySmall = base.displaySmall.copy(fontWeight = FontWeight.Bold, fontSize = 34.sp),
        headlineMedium = base.headlineMedium.copy(fontWeight = FontWeight.Bold),
        headlineSmall = base.headlineSmall.copy(fontWeight = FontWeight.Bold),
        titleLarge = base.titleLarge.copy(fontWeight = FontWeight.SemiBold),
        titleMedium = base.titleMedium.copy(fontWeight = FontWeight.SemiBold),
    )
}

@Composable
fun AppTheme(content: @Composable () -> Unit) {
    MaterialTheme(
        colorScheme = ColorScheme,
        typography = AppTypography,
        content = content,
    )
}
